import { useState } from "react";
import type { TeamPublic, TeamDeleteImpact } from "@dugads/shared";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, AlertTriangle, Loader2 } from "lucide-react";

interface TeamDeleteDialogProps {
  team: TeamPublic;
  impact: TeamDeleteImpact;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

/** Confirmation dialog for deleting a team that has players and/or event
 *  assignments. For empty teams the caller bypasses this dialog and deletes
 *  directly. */
export function TeamDeleteDialog({ team, impact, onClose, onConfirm }: TeamDeleteDialogProps) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typedName, setTypedName] = useState("");
  // 078: require typed-name confirmation when the team is non-empty
  const requiresConfirm = impact.player_count > 0 || impact.event_count > 0;
  const confirmMatches = typedName.trim().toLowerCase() === team.display_name.trim().toLowerCase();
  const canConfirm = !requiresConfirm || confirmMatches;

  const isTrial = team.team_type === "trial";
  const verb = isTrial ? "Dissolve" : "Delete";
  const verbLower = verb.toLowerCase();

  async function handleConfirm() {
    setDeleting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to ${verbLower} team`);
      setDeleting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4" data-dialog="team-delete">
      <Card className="mt-16 w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              {verb} {team.display_name}?
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose} disabled={deleting} data-action="close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            {verb === "Delete" ? "Deleting" : "Dissolving"} this team will remove:
          </p>
          <ul className="flex flex-col gap-1.5 rounded-md border border-border bg-muted/30 p-3 text-sm">
            {impact.player_count > 0 && (
              <li className="flex items-center gap-2">
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive/10 px-1.5 text-xs font-medium text-destructive" data-field="player-count">
                  {impact.player_count}
                </span>
                player{impact.player_count === 1 ? "" : "s"} from the roster
              </li>
            )}
            {impact.event_count > 0 && (
              <li className="flex items-center gap-2">
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive/10 px-1.5 text-xs font-medium text-destructive" data-field="event-count">
                  {impact.event_count}
                </span>
                event{impact.event_count === 1 ? "" : "s"} this team is assigned to
              </li>
            )}
            {impact.match_count > 0 && (
              <li className="flex items-center gap-2">
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive/10 px-1.5 text-xs font-medium text-destructive" data-field="match-count">
                  {impact.match_count}
                </span>
                match{impact.match_count === 1 ? "" : "es"} this team is playing in
              </li>
            )}
          </ul>

          {impact.upcoming_events && impact.upcoming_events.length > 0 && (
            <div className="rounded-md border border-border bg-muted/20 p-3 text-xs" data-region="event-preview">
              <div className="mb-1 font-medium text-muted-foreground">Recent events affected</div>
              <ul className="flex flex-col gap-0.5">
                {impact.upcoming_events.map((ev) => (
                  <li key={ev.event_id} className="flex items-center justify-between gap-2">
                    <span className="truncate">{ev.event_name}</span>
                    <span className="shrink-0 text-muted-foreground">
                      {new Date(ev.starts_at).toLocaleDateString()}
                      {ev.event_status !== "published" && (
                        <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] uppercase text-amber-700">
                          {ev.event_status}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Players, coaches, matches and the events themselves stay in the system -- only this team's links to them are removed.
          </p>

          {requiresConfirm && (
            <div className="space-y-1.5">
              <Label htmlFor="confirm-team-name" className="text-xs">
                Type <strong>{team.display_name}</strong> to confirm
              </Label>
              <Input
                id="confirm-team-name"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                disabled={deleting}
                data-field="confirm-name"
                autoComplete="off"
              />
            </div>
          )}

          {error && <p className="text-sm text-destructive" data-error>{error}</p>}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={deleting} data-action="cancel">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={deleting || !canConfirm}
              data-action="confirm-delete"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> {verb === "Delete" ? "Deleting…" : "Dissolving…"}
                </>
              ) : (
                verb
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
